/**
 * This component is a list of customer apps for use on the My Apps page.
 * - For unauth customers, this is the "My projects" page.
 * 
 * Prior to July 2023, depending on if there were auth only subscriptions or not, the page would display a 
 * either a tabbed experience (purchased subscriptions | shared logins) or a single page with just the purchased
 * subscriptions.
 * 
 * Now we're moving to more tabs:
 * - Managed for me (purchased subscriptions)
 * - Shared by me (shared logins)
 * - Services (reseller services that I'm using)
 * - Orders (SO history)
 * 
 * For customers, we'll start by showing all of these tabs regardless of whether or not the user has data for some of these.
 * Resellers will retain a single page experience.  This may eventually be split into a separate component; for now, in this
 * code, we'll split different persona cases more clearly.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';
import { Divider, Grid, Paper, Tab, Tabs, Typography } from '@mui/material';

import Subscriptions from '../account/Subscriptions';
import AuthOnlySubscriptions from '../account/AuthOnlySubscriptions';
import SubscriptionsSignedUp from '../account/SubscriptionsSignedUp';
import SubscriptionOrders from '../customer/SubscriptionOrders';
import Communications from '../notification/Communications';
import Services from '../account/Services';
import LoadingSpinner from '../ui/LoadingSpinner';
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';

import { DesignServices, ManageAccounts, Receipt, Share } from '@mui/icons-material';

import BindCardUnattachedFetch from '../customer/BindCardUnattachedFetch';
import { or } from 'ajv/dist/compile/codegen';

const abEnvironment = new AbEnvironment();

const CustomerAppListQuery = graphql`
query CustomerAppListQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    accountType
    customerStatus
    accountStatus
    lastSoSent
    useBudgets
    accountTByParentId {
      accountName
      accountId
    }
    custPayCount: customerAppTsByCustomerAccountId (condition: { paymentOrder:0}) {
      totalCount
    }
    custPayCountBackup: customerAppTsByCustomerAccountId (condition: { paymentOrder:1}) {
      totalCount
    }
    resellerSignupCount: customerAppTsByCustomerAccountId (condition: { paymentOrder:5}) {
      totalCount
    }
    customerServiceTsByCustomerAccountId {
      totalCount
      nodes {
        customerServiceId
        billingFrequency
        soAccepted
      }
    }
  }
  allBindCardTs(condition: {usingAccountId: $accountId, bcStatus: 4}) {
    totalCount
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
  },
  paper: {
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
    textAlign: 'left',
    color: theme.palette.text.secondary,
    maxWidth: 1154,
    marginTop: 16,
  },
  flex: {
    flex: 1,
  },
  tabroot: {
    flexGrow: 1,
    margin: 0,
    maxWidth: 1200,
    //backgroundColor: theme.palette.primary.light,
    borderBottom: '2px solid #dedede'
  },
  managedByLabel: {
    color: theme.palette.secondary.main,
    // fontWeight: 'bold',
  },
});

class CustomerAppList extends Component {

  constructor(props) {
    super(props);

    this.state = {
      subscriptionTabValue: 0
    }
  }

  componentDidMount() {
    //this code here is to get the menu back when magic link redirects here.
    let fromResumeCustomer = localStorage.getItem('fromstepperresumecustomer');
    if (fromResumeCustomer) {
      localStorage.removeItem('fromstepperresumecustomer');
      window.location.reload();
      return;
    }

    // Is there something in local storage?
    let theValue = 0;
    let tabval = localStorage.getItem('caSubscriptionTabVal');
    if (tabval !== null) {
      theValue = parseInt(tabval, 10);
    }
    
    this.unattachedBindCardProcess();

    this.setState({ subscriptionTabValue: theValue });
  }

  /*
  * See if there are any unattached bind cards.  If the ps_count > 0 call api to attach them.
  * This is here just as extra hook.  This same process happens in PaymentForm.js and ACHForm.js.
  * In other words, the only way for ps_count to be > 0 and there be unattached bind cards is
  * if there was some sort of failure attaching cards first time around.
  */
  unattachedBindCardProcess = () => {
    const { callPartnerApi, logError } = this.props.auth;
    let aPjwt = abEnvironment.getPjwt();

    let acctId = abEnvironment.getUserAttribute("a_id");

    if (acctId) {
      BindCardUnattachedFetch.fetch(acctId).toPromise()
      .then(bcuf => {
         if (bcuf.getbindcardsunattached && bcuf.getbindcardsunattached.nodes && bcuf.getbindcardsunattached.nodes.length > 0) {
          
          // Call checkAndAttachBindcards 
          // This backend operation will now see if any bind cards are eligible to be attached for any subscriptions created
          let payload = {
            operation: "checkAndAttachBindcards",
            params: {
              pJwt:        aPjwt,
              accountId:   acctId
            }
          };

          return callPartnerApi('appbind/generic',
                        'POST',
                        payload);
         } else {
           //console.log("There are no unattached bind cards.")
         }

      })
      .catch(err => {
        //only thing to cause this is perhaps outdated DB structure.
        logError('Exception in CustomerAppList.js: ' + err);
      })
 
    }
  }

  handleSubscriptionTabChange = (event, value) => {
    this.setState({ subscriptionTabValue: value });
    localStorage.setItem('caSubscriptionTabVal', value);
  }

  render() {
    const { needAssent, isAuthenticatedCustomer, isAuthenticatedReseller, isUnauthenticatedCustomer, isAuthenticatedAppBind, logError } = this.props.auth;
    const classes = this.props.classes;

    if (needAssent()) {
      return (
        <Redirect to='/home' />
      );
    }

    if (isAuthenticatedAppBind())
    {
      return (
        <div>
          <PageTitle title="My apps"/>
          <p>
            App Summary goes here
          </p>
        </div>
      );
    }
    else if (isAuthenticatedReseller() || isAuthenticatedCustomer() || isUnauthenticatedCustomer())
    {
      let acctId = abEnvironment.getUserAttribute("a_id");
      let custAppLink = "subscriptioncreate?accountId=" + acctId;

      let paymentSourceCount = 0;
      if (abEnvironment.getUserAttribute("ps_count")) {
        paymentSourceCount = parseInt(abEnvironment.getUserAttribute("ps_count"), 10);
      } else {
        paymentSourceCount = 0;
      }

      //
      // There are 4 different experiences here:
      // 1. Unauthenticated customer
      // 2. Authenticated customer that is under a reseller
      // 3. Authenticated customer that is independent
      // 4. Reseller
      //
      // Although there is overlap for in experience, we'll split this into four partially duplicative
      // blocks in order to clarify maintenance.
      //
      return (
        <div className={classes.root}>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={CustomerAppListQuery}
            variables={{accountId: acctId}}
            render={({error, props})=> {
              if (error) {
                logError(error.message);
                return (
                  <ServerError/>
                );
              } else if (props) {

                let theAccount = props.accountTByAccountId;
                let parentAccount = theAccount.accountTByParentId;

                // For customers, if useBudgets is on, then show 4 tabs.  If off, only show the 2 subscriptions tabs.
                // - that is, if budgets are off, services and orders aren't relevant.
                let useBudgets = false;
                if (theAccount.useBudgets != null && theAccount.useBudgets > 0) {
                  useBudgets = true;
                }

                // Determine if an authenticated customer is self-managed or not.  That is, the customer's
                // parent is not a reseller.  There's also a customer status setting on account to indicate
                // transferred.  TODO: determine if this should be removed.
                let selfManagedCustomer = false;
                if (isAuthenticatedCustomer()) {
                  // special case of a transferred customer - they get to manage their subscriptions.  TODO: remove this?
                  if (theAccount.accountType === 4 && theAccount.customerStatus === 2) {
                    selfManagedCustomer = true;
                  }

                  if (parentAccount == null || parentAccount.accountType === 0) {
                    selfManagedCustomer = true;
                  }
                }

                let custPayCount = theAccount.custPayCount.totalCount + theAccount.custPayCountBackup.totalCount;
                let resellerSignupCount = theAccount.resellerSignupCount.totalCount;
                let serviceCount = theAccount.customerServiceTsByCustomerAccountId.totalCount;

                if (isUnauthenticatedCustomer()) {

                  // ----------------------------------------------------------
                  // ---------- Unauthenticated customer experience ----------
                  // ----------------------------------------------------------


                  let managedByName = parentAccount.accountName;
  
                  let pageTitle = "My project with ";
                  let pageTitleSuffix = managedByName;

                  let pageSubtitle = "Subscriptions that have been provisioned for me, and subscriptions that I've shared access to.";
                  if (serviceCount > 0) {
                    pageSubtitle = "Subscriptions and services that have been provisioned for me, and subscriptions that I've shared access to.";
                  }

                  /*
                  let pageSubtitle = "Subscriptions and services that have been provisioned for me, and subscriptions that I've shared access to.";
                  if (useBudgets !== true) {
                    pageSubtitle = "Subscriptions that have been provisioned for me, and subscriptions that I've shared access to.";
                  }
                  */

                  let managedTab = 0;
                  let sharedTab = 1;
                  let servicesTab = null;
                  let ordersTab = 2;
                  if (serviceCount > 0) {
                    servicesTab = 2;
                    ordersTab = 3;
                  }

                  return (
                    <React.Fragment>
                      <PageTitle title={pageTitle} titleSuffix={pageTitleSuffix} subtitle={pageSubtitle}/>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={this.state.subscriptionTabValue}
                            onChange={this.handleSubscriptionTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{ root: classes.tabroot, scrollButtons: classes.tabScrollButton }}
                          >
                            <Tab label="Managed for me" icon={<ManageAccounts/>}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            <Tab label="Shared by me" icon={<Share/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            {servicesTab != null &&
                            <Tab label="Services" icon={<DesignServices/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                            <Tab label="Orders" icon={<Receipt/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                          </Tabs>
                        </Grid>

                        {this.state.subscriptionTabValue === managedTab &&
                        <React.Fragment>
                          {(custPayCount === 0 || resellerSignupCount > 0) &&
                          <Grid item xs={12}>
                            <Paper className={classes.paper}>
                              <Grid container spacing={2} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <Typography variant="h6">Subscriptions managed for me</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                        <Grid item>
                                          <Typography variant="body2">These subscriptions have been provisioned for me by&nbsp;</Typography>
                                        </Grid>
                                        <Grid item>
                                          <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                        </Grid>
                                        <Grid item>
                                          <Typography variant="body2">.</Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>                                
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}><Divider/></Grid>
                                <Grid item xs={12}>
                                  <SubscriptionsSignedUp customerAccountId={acctId}
                                                  parentAccount={parentAccount}
                                                  myAccount={theAccount}
                                                  pg="myApps"
                                                  auth={this.props.auth}
                                                  history={this.props.history}
                                                  custAppLink={custAppLink}
                                                  pscount={paymentSourceCount}
                                                  transferredCustomer={selfManagedCustomer}/>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>}
                          {custPayCount > 0 &&
                          <Grid item xs={12}>
                            <Paper className={classes.paper}>
                              <Grid container spacing={2} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <Typography variant="h6">Subscriptions expensed for me</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                        <Grid item>
                                          <Typography variant="body2">These subscriptions have been purchased and expensed to me by&nbsp;</Typography>
                                        </Grid>
                                        <Grid item>
                                          <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                        </Grid>
                                        <Grid item>
                                          <Typography variant="body2">.</Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>                                
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}><Divider/></Grid>
                                <Grid item xs={12}>
                                  <Subscriptions customerAccountId={acctId}
                                                parentAccount={parentAccount}
                                                myAccount={theAccount}
                                                pg="myApps"
                                                auth={this.props.auth}
                                                history={this.props.history}
                                                custAppLink={custAppLink}
                                                pscount={paymentSourceCount}
                                                transferredCustomer={selfManagedCustomer}/>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>}
                        </React.Fragment>}

                        {this.state.subscriptionTabValue === sharedTab &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Subscriptions that I'm sharing</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body2">I already have these subscription, and&nbsp;</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2">&nbsp;has asked me to share access to them.</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>                                
                                </Grid>
                              </Grid>
                              <Grid item xs={12}><Divider/></Grid>
                              <Grid item xs={12}>
                                <AuthOnlySubscriptions customerAccountId={acctId}
                                              parentAccount={parentAccount}
                                              myAccount={theAccount}
                                              pg="myApps"
                                              auth={this.props.auth}
                                              history={this.props.history}
                                              custAppLink={custAppLink}
                                              pscount={paymentSourceCount}
                                              transferredCustomer={selfManagedCustomer}/>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>}

                        {this.state.subscriptionTabValue === servicesTab &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Services that I'm being provided</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body2">These services are provided by&nbsp;</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2">.</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>                                
                                </Grid>
                              </Grid>
                              <Grid item xs={12}><Divider/></Grid>
                              <Grid item xs={12}>
                                <Services customerAccountId={acctId}
                                          pg="myApps"
                                          auth={this.props.auth}
                                          history={this.props.history}
                                          custAppLink={custAppLink}
                                          pscount={paymentSourceCount}
                                          transferredCustomer={selfManagedCustomer}/>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>}

                        {/* Subscription orders has its own header, so just wrap in paper*/}
                        {this.state.subscriptionTabValue === ordersTab &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <SubscriptionOrders accountId={acctId}
                                                parentAccount={parentAccount}
                                                pg="myApps"
                                                auth={this.props.auth} history={this.props.history}                                            
                                                account={theAccount}
                                                accountType={props.accountTByAccountId.accountType}
                                                location={this.props.location} />
                          </Paper>
                        </Grid>}
                      </Grid>
                    </React.Fragment>
                  );
                } else if (isAuthenticatedCustomer() && selfManagedCustomer === false) {

                  // ----------------------------------------------------------------------------
                  // --------- Authenticated customer experience - managed by reseller ----------
                  // ----------------------------------------------------------------------------

                  let managedByName = parentAccount.accountName;
  
                  let pageTitle = "My project with ";
                  let pageTitleSuffix = managedByName;
                  let pageSubtitle = "Subscriptions and services that have been provisioned for me, and subscriptions that I've shared access to.";
                  if (useBudgets !== true) {
                    pageSubtitle = "Subscriptions that have been provisioned for me, and subscriptions that I've shared access to.";
                  }
  
                  return (
                    <React.Fragment>
                      <PageTitle title={pageTitle} titleSuffix={pageTitleSuffix} subtitle={pageSubtitle}/>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={this.state.subscriptionTabValue}
                            onChange={this.handleSubscriptionTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{ root: classes.tabroot, scrollButtons: classes.tabScrollButton }}
                          >
                            <Tab label="Managed for me" icon={<ManageAccounts/>}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            <Tab label="Shared by me" icon={<Share/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            {useBudgets === true &&
                            <Tab label="Services" icon={<DesignServices/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                            {useBudgets === true &&
                            <Tab label="Orders" icon={<Receipt/>} 
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                          </Tabs>
                        </Grid>

                        {this.state.subscriptionTabValue === 0 &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Subscriptions managed for me</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body2">These subscriptions have been provisioned for me by&nbsp;</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2">.</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>                                
                                </Grid>
                              </Grid>
                              <Grid item xs={12}><Divider/></Grid>
                              <Grid item xs={12}>
                                <Subscriptions customerAccountId={acctId}
                                               parentAccount={parentAccount}
                                               myAccount={theAccount}
                                               pg="myApps"
                                               auth={this.props.auth}
                                               history={this.props.history}
                                               custAppLink={custAppLink}
                                               pscount={paymentSourceCount}
                                               transferredCustomer={selfManagedCustomer}/>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>}

                        {this.state.subscriptionTabValue === 1 &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Subscriptions that I'm sharing</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body2">I already have these subscription, and&nbsp;</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2">&nbsp;has asked me to share access to them.</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>                                
                                </Grid>
                              </Grid>
                              <Grid item xs={12}><Divider/></Grid>
                              <Grid item xs={12}>
                                <AuthOnlySubscriptions customerAccountId={acctId}
                                              parentAccount={parentAccount}
                                              myAccount={theAccount}
                                              pg="myApps"
                                              auth={this.props.auth}
                                              history={this.props.history}
                                              custAppLink={custAppLink}
                                              pscount={paymentSourceCount}
                                              transferredCustomer={selfManagedCustomer}/>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>}

                        {this.state.subscriptionTabValue === 2 && useBudgets === true &&
                        <Grid item xs={12}>
                          <Paper className={classes.paper}>
                            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Grid container spacing={0}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Services that I'm being provided</Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container direction="row" justify="flex-start" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body2">These services are provided by&nbsp;</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2" className={classes.managedByLabel}>{managedByName}</Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="body2">.</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>                                
                                </Grid>
                              </Grid>
                              <Grid item xs={12}><Divider/></Grid>
                              <Grid item xs={12}>
                                <Services customerAccountId={acctId}
                                          pg="myApps"
                                          auth={this.props.auth}
                                          history={this.props.history}
                                          custAppLink={custAppLink}
                                          pscount={paymentSourceCount}
                                          transferredCustomer={selfManagedCustomer}/>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>}

                        {/* Subscription orders has its own header, so just wrap in paper*/}
                        {this.state.subscriptionTabValue === 3 && useBudgets === true &&
                        <Grid item xs={12}>
                          <Grid container spacing={0} direction="row" justifyContent="flex-start">
                            <Grid item xs={12}>
                              <Paper className={classes.paper}>
                                <SubscriptionOrders accountId={acctId}
                                                    parentAccount={parentAccount}
                                                    pg="myApps"
                                                    auth={this.props.auth} history={this.props.history}                                            
                                                    account={theAccount}
                                                    accountType={props.accountTByAccountId.accountType}
                                                    location={this.props.location} />
                              </Paper>
                            </Grid>

                            <Grid item xs={12}>
                              <Paper className={classes.paper}>
                                <Communications auth={this.props.auth} history={this.props.history}/>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Grid>}
                      </Grid>
                    </React.Fragment>
                  );

                } else if (isAuthenticatedCustomer() && selfManagedCustomer === true) {

                  // ----------------------------------------------------------
                  // ------------ Authenticated customer experience -----------
                  // ----------------------------------------------------------

                  let parentAccount = theAccount.accountTByParentId;
                  let pageTitle = "My subscriptions";
                  let pageSubtitle = "What subscriptions do I have?";

                  return (
                    <React.Fragment>
                      <PageTitle title={pageTitle} subtitle={pageSubtitle}/>
                      <Paper className={classes.paper}>
                        <Subscriptions customerAccountId={acctId}
                                       parentAccount={parentAccount}
                                       myAccount={theAccount}
                                       pg="myApps"
                                       auth={this.props.auth}
                                       history={this.props.history}
                                       custAppLink={custAppLink}
                                       pscount={paymentSourceCount}
                                       transferredCustomer={selfManagedCustomer}/>
                      </Paper>
                    </React.Fragment>
                  );

                } else if (isAuthenticatedReseller()) {

                  // -----------------------------------------
                  // ---------- Reseller experience ----------
                  // -----------------------------------------

                  let parentAccount = theAccount.accountTByParentId;
                  let pageTitle = "My subscriptions";
                  let pageSubtitle = "What subscriptions do I have?";

                  return (
                    <React.Fragment>
                      <PageTitle title={pageTitle} subtitle={pageSubtitle}/>
                      <Paper className={classes.paper}>
                        <Subscriptions customerAccountId={acctId}
                                      parentAccount={parentAccount}
                                      myAccount={theAccount}
                                      pg="myApps"
                                      auth={this.props.auth}
                                      history={this.props.history}
                                      custAppLink={custAppLink}
                                      pscount={paymentSourceCount}
                                      transferredCustomer={selfManagedCustomer}/>
                      </Paper>
                    </React.Fragment>
                  );
                }
              };

              // If we're here, we're still loading
              return <LoadingSpinner/>
            }}
          />
        </div>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }
}

export default withStyles(styleSheet)(CustomerAppList);
